/**
 * Form Validation Utility
 * Provides comprehensive form validation with field-level error handling
 */

import { getCountryByCode, usePostalCodeValidation } from "postal-code-checker";

const { validatePostalCode } = usePostalCodeValidation();

/**
 * Validates a postcode for a specific country using the postal-code-checker library
 * @param {string} postcode - The postcode to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validate_postcode = (postcode: string) => {
  try {
    console.log("Starting postcode validation:", postcode);
    const user_country = sessionStorage
      .getItem("user_country_session")
      ?.toUpperCase();

    if (!user_country) {
      return false;
    }

    const country = getCountryByCode(user_country as any);
    if (!country) {
      return true;
    }

    const trimmed_postcode = postcode.trim();
    if (!trimmed_postcode) return false;

    return validatePostalCode(country.countryCode as any, trimmed_postcode);
  } catch (error) {
    console.warn("Postcode validation error:", error);
    return false;
  }
};

/**
 * Validates a single form field and shows/hides error messages
 * @param {HTMLInputElement | HTMLSelectElement} field - The field to validate
 * @returns {boolean} True if field is valid, false otherwise
 */
export const validate_field = (
  field: HTMLInputElement | HTMLSelectElement,
): boolean => {
  // Find the error container for this field
  const error_container = document.querySelector(`#${field.name}-error`);

  // Handle styling for different field types
  if (field.type === "checkbox") {
    // For custom checkboxes, find the wrapper and handle styling differently
    const checkbox_wrapper = field.closest(".custom-checkbox-wrapper");
    if (checkbox_wrapper) {
      // Remove error styling from custom checkbox wrapper
      checkbox_wrapper.classList.remove("border-red-500");
    } else {
      // Standard checkbox styling
      field.classList.remove("border-red-500", "bg-red-50");
    }

    // Also remove error styling from custom checkbox button if present
    const checkbox_button = field
      .closest(".custom-checkbox-wrapper")
      ?.querySelector(".custom-checkbox-button");
    if (checkbox_button) {
      checkbox_button.classList.remove("!border-red-500");
    }
  } else {
    // Standard field styling
    field.classList.remove("border-red-500", "bg-red-50");
  }

  // Hide error container
  if (error_container) {
    error_container.classList.add("hidden");
    error_container.classList.remove("block");
  }

  let is_valid = true;

  // Check if required field is empty
  if (field.hasAttribute("required")) {
    if (field.type === "checkbox") {
      // For checkboxes, check if they are checked
      if (!(field as HTMLInputElement).checked) {
        is_valid = false;
      }
    } else {
      // For other field types, check if value is empty
      if (!field.value || field.value.trim() === "") {
        is_valid = false;
      }
    }
  }

  // Email validation
  if (field.type === "email" && field.value) {
    const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email_regex.test(field.value)) {
      is_valid = false;
    }
  }

  // Phone validation (basic) - fixed regex escaping
  if (field.type === "tel" && field.value) {
    const phone_regex = /^[+]?[1-9][\d]{0,15}$/;
    if (!phone_regex.test(field.value.replace(/[\s\-()]/g, ""))) {
      is_valid = false;
    }
  }

  // Postcode validation for billing_postcode field
  if (field.name === "billing_postcode" && field.value) {
    is_valid = validate_postcode(field.value);
  }

  // Show error if invalid
  if (!is_valid) {
    // Add error styling based on field type
    if (field.type === "checkbox") {
      // For custom checkboxes, add error styling to wrapper
      const checkbox_wrapper = field.closest(".custom-checkbox-wrapper");
      if (checkbox_wrapper) {
        checkbox_wrapper.classList.add("border-red-500");
      } else {
        // Standard checkbox styling
        field.classList.add("border-red-500", "bg-red-50");
      }

      // Also add error styling to custom checkbox button if present
      const checkbox_button = field
        .closest(".custom-checkbox-wrapper")
        ?.querySelector(".custom-checkbox-button");
      if (checkbox_button) {
        checkbox_button.classList.add("!border-red-500");
      }
    } else {
      // Standard field styling
      field.classList.add("border-red-500", "bg-red-50");
    }

    // Show error container
    if (error_container) {
      error_container.classList.remove("hidden");
      error_container.classList.add("block");
    }
  }

  return is_valid;
};

/**
 * Validates all form fields and returns validation result
 * @param {HTMLFormElement} form - The form to validate
 * @returns {boolean} True if all fields are valid, false otherwise
 */
export const validate_all_fields = (form: HTMLFormElement): boolean => {
  if (!form) return false;

  const fields = form.querySelectorAll(
    "input[required], select[required], textarea[required]",
  );
  let all_valid = true;

  fields.forEach((field) => {
    const is_valid = validate_field(
      field as HTMLInputElement | HTMLSelectElement,
    );

    if (!is_valid) {
      all_valid = false;
    }
  });

  return all_valid;
};

/**
 * Clears error styling and messages from a field
 * @param {HTMLInputElement | HTMLSelectElement} field - The field to clear
 */
export const clear_field_error = (
  field: HTMLInputElement | HTMLSelectElement,
): void => {
  // Find the error container for this field
  const error_container = document.querySelector(`#${field.name}-error`);

  // Handle styling for different field types
  if (field.type === "checkbox") {
    // For custom checkboxes, find the wrapper and clear styling
    const checkbox_wrapper = field.closest(".custom-checkbox-wrapper");
    if (checkbox_wrapper) {
      // Remove error styling from custom checkbox wrapper
      checkbox_wrapper.classList.remove("border-red-500");
    } else {
      // Standard checkbox styling
      field.classList.remove("border-red-500", "bg-red-50");
    }

    // Also remove error styling from custom checkbox button if present
    const checkbox_button = field
      .closest(".custom-checkbox-wrapper")
      ?.querySelector(".custom-checkbox-button");
    if (checkbox_button) {
      checkbox_button.classList.remove("!border-red-500");
    }
  } else {
    // Standard field styling
    field.classList.remove("border-red-500", "bg-red-50");
  }

  // Hide error container
  if (error_container) {
    error_container.classList.add("hidden");
    error_container.classList.remove("block");
  }
};

/**
 * Sets up real-time validation for form fields
 * @param {HTMLFormElement} form - The form to set up validation for
 */
export const setup_real_time_field_validation = (
  form: HTMLFormElement,
): void => {
  if (!form) return;

  const fields = form.querySelectorAll("input, select, textarea");

  fields.forEach((field) => {
    const field_element = field as HTMLInputElement | HTMLSelectElement;

    // Validate on blur (when user leaves the field)
    field.addEventListener("blur", () => {
      validate_field(field_element);
    });

    // Clear errors on input/change
    field.addEventListener("input", () => {
      clear_field_error(field_element);
    });

    field.addEventListener("change", () => {
      clear_field_error(field_element);
    });

    // Special handling for checkboxes - validate immediately on change
    if (field_element.type === "checkbox") {
      field.addEventListener("change", () => {
        validate_field(field_element);
      });
    }
  });
};
